<template>
  <header class="hero">
    <div class="hero__background">
      <div class="hero__parallax-layer">
        <div class="hero__decoration hero__decoration--top-left"></div>
        <div class="hero__decoration hero__decoration--bottom-right"></div>
      </div>

      <div class="hero__parallax-layer hero__parallax-layer--mid">
        <div class="hero__decoration hero__decoration--center"></div>
      </div>
    </div>

    <div class="hero__container">
      <div class="hero__grid">
        <section class="hero__content">
          <div class="status-badge">
            <div class="status-badge__dot"></div>
            <span class="status-badge__text">{{ $t('page.hero.status') }}</span>
          </div>
          <p class="hero__description" v-html="$t('page.hero.description')"></p>
          <header class="hero__header">
            <h1 ref="heroTitle" class="hero__title">
              <span class="hero__title-accent"
                >{{ $t('page.hero.title.first') }}
                <span class="hero__title-italic">{{ $t('page.hero.title.highlight') }}</span>
                {{ $t('page.hero.title.last') }}
              </span>
            </h1>
          </header>
        </section>

        <aside class="hero__media">
          <figure class="profile-card">
            <div class="profile-card__image-wrapper">
              <img
                :src="profileImage"
                alt="Juan Lias - Desarrollador Full Stack"
                class="profile-card__image"
                fetchpriority="high"
              />
              <div class="profile-card__overlay profile-card__overlay--highlight"></div>
              <div class="profile-card__overlay profile-card__overlay--shadow"></div>

              <div class="profile-card__holo">
                <div class="profile-card__holo-gradient"></div>
                <div class="profile-card__holo-sparkle"></div>
                <div class="profile-card__holo-shine"></div>
              </div>
            </div>
          </figure>
        </aside>
      </div>
    </div>

    <div class="scroll-indicator">
      <div class="scroll-indicator__content">
        <span class="scroll-indicator__text">{{ $t('page.hero.scroll') }}</span>
        <div class="scroll-indicator__line">
          <div class="scroll-indicator__dot"></div>
        </div>
      </div>
    </div>
    
    <TiltIndicator :show="showTiltIndicator" />
  </header>
</template>
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { gsap } from 'gsap'
import profileImageDark from '@/assets/images/juan-lias-2.webp'
import profileImageLight from '@/assets/images/juan-lias-white.webp'
import { useTheme } from '@/composables/useTheme'
import TiltIndicator from '@/components/common/TiltIndicator.vue'

// Obtener estado del tema
const { isDark } = useTheme()

// Imagen de perfil reactiva según el tema
const profileImage = computed(() => (isDark.value ? profileImageDark : profileImageLight))

// ============================================================================
// CONFIGURACIÓN Y CONSTANTES
// ============================================================================
const ANIMATION_CONFIG = {
  parallax: {
    layers: [
      { selector: '.hero__parallax-layer--far', speed: 0.3 },
      { selector: '.hero__parallax-layer--mid', speed: 0.5 },
      { selector: '.hero__decoration--top-left', speed: 0.4 },
      { selector: '.hero__decoration--bottom-right', speed: 0.6 },
    ],
    profileCard: { selector: '.profile-card', speed: 0.2 },
    center: { selector: '.hero__decoration--center', speed: 0.8 },
  },
  timing: {
    entrance: {
      background: 0,
      statusBadge: 0.3,
      description: 0.5,
      header: 0.7,
      actions: 1.1,
      profileCard: 0.6,
      profileImage: 0.8,
      decorationTop: 1.2,
      decorationBottom: 1.3,
      experienceBadge: 1.4,
      profileBadge: 1.5,
    },
  },
  mobile: {
    breakpoint: 768,
  },
}

// ============================================================================
// ESTADO Y REFS
// ============================================================================
const heroTitle = ref(null)
const showTiltIndicator = ref(false)

// Cache de elementos DOM
let elements = {}
let masterTimeline = null
let floatingAnimations = []

// Estado para Device Orientation (mobile)
let hasOrientationPermission = false
let initialOrientation = { beta: 0, gamma: 0 }
let isOrientationCalibrated = false

// ============================================================================
// UTILIDADES
// ============================================================================
const isMobile = () => window.innerWidth < ANIMATION_CONFIG.mobile.breakpoint

const cacheElements = () => {
  elements = {
    hero: document.querySelector('.hero'),
    statusBadge: document.querySelector('.status-badge'),
    description: document.querySelector('.hero__description'),
    heroHeader: document.querySelector('.hero__header'),
    heroActions: document.querySelector('.hero__actions'),
    profileCard: document.querySelector('.profile-card'),
    profileCardWrapper: document.querySelector('.profile-card__image-wrapper'),
    profileImage: document.querySelector('.profile-card__image'),
    profileBadge: document.querySelector('.profile-card__badge'),
    experienceBadge: document.querySelector('.experience-badge'),
    decorationTop: document.querySelector('.profile-card__decoration--top'),
    decorationBottom: document.querySelector('.profile-card__decoration--bottom'),
    backgroundDecorations: document.querySelectorAll('.hero__decoration'),
    parallaxLayers: document.querySelectorAll('.hero__parallax-layer'),
    accentElement: heroTitle.value?.querySelector('.hero__title-accent'),
    // Elementos del efecto holográfico
    holoGradient: document.querySelector('.profile-card__holo-gradient'),
    holoSparkle: document.querySelector('.profile-card__holo-sparkle'),
    holoShine: document.querySelector('.profile-card__holo-shine'),
    scrollIndicator: document.querySelector('.scroll-indicator'),
  }
}

// ============================================================================
// PARALLAX EN SCROLL
// ============================================================================
const handleScroll = () => {
  const scrolled = window.pageYOffset
  const mobile = isMobile()
  const { parallax } = ANIMATION_CONFIG

  // Ocultar scroll indicator al hacer scroll
  if (elements.scrollIndicator) {
    const opacity = Math.max(0, 1 - scrolled / 300)
    gsap.set(elements.scrollIndicator, { opacity, y: scrolled * 0.3 })
  }

  // Animación de blur y movimiento hacia arriba para todo el contenido de texto
  // Ajustado para que el fade sea más gradual y empiece más tarde
  const progress = Math.min(1, scrolled / 500) // 0 a 1 en 500px de scroll (antes 400px)
  const blurAmount = progress * 6 // Hasta 6px de blur (antes 8px)
  const moveY = progress * -120 // Mover hacia arriba (antes -150)
  const opacity = Math.max(0.05, 1 - progress * 0.95) // Casi 0 al final

  // heroHeader
  if (elements.heroHeader) {
    gsap.set(elements.heroHeader, {
      filter: `blur(${blurAmount}px)`,
      y: moveY,
      opacity,
      force3D: true,
    })
  }

  // description
  if (elements.description) {
    gsap.set(elements.description, {
      filter: `blur(${blurAmount * 0.85}px)`,
      y: moveY * 0.75,
      opacity,
      force3D: true,
    })
  }

  // statusBadge
  if (elements.statusBadge) {
    gsap.set(elements.statusBadge, {
      filter: `blur(${blurAmount * 0.7}px)`,
      y: moveY * 0.6,
      opacity,
      force3D: true,
    })
  }

  // Parallax para elementos estándar
  parallax.layers.forEach(({ selector, speed }) => {
    const element = document.querySelector(selector)
    if (element) {
      gsap.set(element, { y: -(scrolled * speed), force3D: true })
    }
  })

  // Profile card - movimiento hacia arriba
  if (elements.profileCard && !mobile) {
    const moveCardY = progress * -120 // Mover hacia arriba
    const cardOpacity = Math.max(0, 1 - progress * 0.95) // Casi 0 al final
    gsap.set(elements.profileCard, {
      y: moveCardY - scrolled * parallax.profileCard.speed,
      opacity: cardOpacity,
      force3D: true,
    })
  }

  // Elemento center (tiene transform CSS especial)
  const centerElement = document.querySelector(parallax.center.selector)
  if (centerElement) {
    gsap.set(centerElement, { y: -(scrolled * parallax.center.speed), force3D: true })
  }

  // Hide tilt indicator on scroll
  if (showTiltIndicator.value && scrolled > window.innerHeight * 0.5) {
    showTiltIndicator.value = false
  }
}

// ============================================================================
// ANIMACIONES DE ENTRADA
// ============================================================================
const setInitialStates = () => {
  const {
    statusBadge,
    description,
    heroHeader,
    heroActions,
    profileCard,
    profileImage,
    profileBadge,
    experienceBadge,
    decorationTop,
    decorationBottom,
    backgroundDecorations,
    scrollIndicator,
  } = elements

  // Utilidad para filtrar elementos nulos
  const filterNull = (arr) => arr.filter(Boolean)

  // Contenido de texto
  const textElements = filterNull([statusBadge, description, heroHeader, heroActions])
  if (textElements.length) {
    gsap.set(textElements, { opacity: 0, y: 60, filter: 'blur(10px)' })
  }

  // Profile card
  if (profileCard) {
    gsap.set(profileCard, {
      opacity: 0,
      scale: 0.8,
      rotateY: -15,
      rotateX: 5,
      transformPerspective: 1200,
    })
  }

  if (profileImage) {
    gsap.set(profileImage, {
      scale: 1.3,
      filter: 'grayscale(100%) blur(5px)',
    })
  }

  // Badges
  const badges = filterNull([profileBadge, experienceBadge])
  if (badges.length) {
    gsap.set(badges, { opacity: 0, y: 30, scale: 0.8 })
  }

  // Scroll indicator
  if (scrollIndicator) {
    gsap.set(scrollIndicator, { opacity: 0, y: 20 })
  }

  // Decoraciones
  const decorations = filterNull([decorationTop, decorationBottom])
  if (decorations.length) {
    gsap.set(decorations, { opacity: 0, scale: 0 })
  }

  if (backgroundDecorations?.length) {
    gsap.set(backgroundDecorations, { opacity: 0, scale: 0.5 })
  }
}

const createEntranceTimeline = () => {
  const { timing } = ANIMATION_CONFIG
  const t = timing.entrance
  const {
    statusBadge,
    description,
    heroHeader,
    heroActions,
    profileCard,
    profileImage,
    profileBadge,
    experienceBadge,
    decorationTop,
    decorationBottom,
    backgroundDecorations,
  } = elements

  masterTimeline = gsap.timeline({ defaults: { ease: 'power4.out' } })

  // Fondo
  if (backgroundDecorations?.length) {
    masterTimeline.to(
      backgroundDecorations,
      {
        opacity: (i) => [0.15, 0.1, 0.08][i] || 0.1,
        scale: 1,
        duration: 2,
        stagger: 0.3,
        ease: 'elastic.out(1, 0.5)',
      },
      t.background
    )
  }

  // Contenido de texto
  if (statusBadge) {
    masterTimeline.to(
      statusBadge,
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.8,
        ease: 'back.out(1.7)',
      },
      t.statusBadge
    )
  }
  if (description) {
    masterTimeline.to(
      description,
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 1,
      },
      t.description
    )
  }
  if (heroHeader) {
    masterTimeline.to(
      heroHeader,
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 1.2,
      },
      t.header
    )
  }
  if (heroActions) {
    masterTimeline.to(
      heroActions,
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.8,
        ease: 'back.out(1.4)',
      },
      t.actions
    )
  }

  // Profile card
  if (profileCard) {
    masterTimeline.to(
      profileCard,
      {
        opacity: 1,
        scale: 1,
        rotateY: 0,
        rotateX: 0,
        duration: 1.5,
        ease: 'power3.out',
      },
      t.profileCard
    )
  }
  if (profileImage) {
    masterTimeline.to(
      profileImage,
      {
        scale: 1,
        filter: 'grayscale(100%) blur(0px)',
        duration: 1.8,
        ease: 'power2.out',
      },
      t.profileImage
    )
  }

  // Decoraciones
  if (decorationTop) {
    masterTimeline.to(
      decorationTop,
      {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'elastic.out(1, 0.4)',
      },
      t.decorationTop
    )
  }
  if (decorationBottom) {
    masterTimeline.to(
      decorationBottom,
      {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'elastic.out(1, 0.4)',
      },
      t.decorationBottom
    )
  }

  // Badges
  if (experienceBadge) {
    masterTimeline.to(
      experienceBadge,
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: 'back.out(2)',
      },
      t.experienceBadge
    )
  }
  if (profileBadge) {
    masterTimeline.to(
      profileBadge,
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        ease: 'elastic.out(1, 0.5)',
      },
      t.profileBadge
    )
  }

  // Scroll indicator (aparece al final)
  if (elements.scrollIndicator) {
    masterTimeline.to(
      elements.scrollIndicator,
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power2.out',
      },
      1.8
    )
  }
}

// ============================================================================
// ANIMACIONES CONTINUAS (LOOP)
// ============================================================================
const createFloatingAnimations = () => {
  const { decorationTop, decorationBottom, profileCardWrapper, accentElement } = elements

  // Decoraciones flotantes
  if (decorationTop) {
    floatingAnimations.push(
      gsap.to(decorationTop, {
        y: -15,
        x: 10,
        duration: 3,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: 2,
      })
    )
  }
  if (decorationBottom) {
    floatingAnimations.push(
      gsap.to(decorationBottom, {
        y: 12,
        x: -8,
        duration: 3.5,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: 2.2,
      })
    )
  }
  // Gradiente del título
  if (accentElement) {
    floatingAnimations.push(
      gsap.to(accentElement, {
        backgroundPosition: '100% 50%',
        duration: 4,
        ease: 'none',
        repeat: -1,
        yoyo: true,
        delay: 2,
      })
    )
  }
}

// ============================================================================
// INTERACTIVIDAD CON MOUSE
// ============================================================================
const handleMouseMove = (e) => {
  const {
    hero,
    accentElement,
    profileCard,
    profileCardWrapper,
    backgroundDecorations,
    parallaxLayers,
    holoGradient,
    holoSparkle,
    holoShine,
  } = elements

  if (!hero) return

  const rect = hero.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top
  const xPercent = (x / rect.width) * 100
  const yPercent = (y / rect.height) * 100
  const rotateY = ((x - rect.width / 2) / rect.width) * 15
  const rotateX = -((y - rect.height / 2) / rect.height) * 10

  // Calcular posición relativa al profile card para el efecto holo
  let cardXPercent = 50,
    cardYPercent = 50
  if (profileCard) {
    const cardRect = profileCard.getBoundingClientRect()
    const cardX = e.clientX - cardRect.left
    const cardY = e.clientY - cardRect.top
    cardXPercent = (cardX / cardRect.width) * 100
    cardYPercent = (cardY / cardRect.height) * 100
  }

  // Efecto holográfico - gradiente arcoíris que sigue el mouse
  if (holoGradient) {
    gsap.to(holoGradient, {
      duration: 0.3,
      ease: 'power2.out',
      opacity: 0.7,
      background: `
        linear-gradient(
          ${125 + rotateY * 2}deg,
          transparent 0%,
          rgba(180, 180, 190, 0.2) ${cardXPercent * 0.5}%,
          rgba(220, 220, 230, 0.25) ${cardXPercent}%,
          rgba(160, 160, 170, 0.2) ${cardXPercent * 1.2}%,
          rgba(200, 200, 210, 0.15) ${cardXPercent * 1.5}%,
          transparent 100%
        )
      `,
    })
  }

  // Efecto sparkle/glitter que se mueve
  if (holoSparkle) {
    gsap.to(holoSparkle, {
      duration: 0.2,
      ease: 'power1.out',
      opacity: 0.6,
      backgroundPosition: `${cardXPercent}% ${cardYPercent}%`,
    })
  }

  // Efecto shine/brillo principal
  if (holoShine) {
    gsap.to(holoShine, {
      duration: 0.4,
      ease: 'power2.out',
      opacity: 0.5,
      background: `
        radial-gradient(
          circle at ${cardXPercent}% ${cardYPercent}%,
          rgba(255, 255, 255, 0.4) 0%,
          rgba(255, 255, 255, 0.1) 20%,
          transparent 60%
        )
      `,
    })
  }

  // Gradiente del título
  if (accentElement) {
    gsap.to(accentElement, {
      duration: 0.6,
      ease: 'power2.out',
      backgroundPosition: `${xPercent}% ${yPercent}%`,
    })
  }

  // Rotación 3D de la tarjeta
  if (profileCard) {
    gsap.to(profileCard, {
      rotateY,
      rotateX,
      duration: 0.8,
      ease: 'power2.out',
      transformPerspective: 1200,
    })
  }

  // Efecto de luz en la tarjeta
  if (profileCardWrapper) {
    gsap.to(profileCardWrapper, {
      background: `linear-gradient(${135 + rotateY}deg, var(--color-charcoal) 0%, var(--color-deep-black) 100%)`,
      duration: 0.3,
    })
  }

  // Parallax de decoraciones
  if (backgroundDecorations?.length) {
    backgroundDecorations.forEach((decoration, index) => {
      const speed = (index + 1) * 0.025
      gsap.to(decoration, {
        duration: 1.2,
        ease: 'power2.out',
        x: (x - rect.width / 2) * speed,
        y: (y - rect.height / 2) * speed,
      })
    })
  }

  // Parallax de capas
  if (parallaxLayers?.length) {
    parallaxLayers.forEach((layer, index) => {
      const speed = (index + 1) * 0.018
      gsap.to(layer, {
        duration: 1.4,
        ease: 'power2.out',
        x: (x - rect.width / 2) * speed,
        y: (y - rect.height / 2) * speed,
      })
    })
  }
}

const handleMouseLeave = () => {
  const {
    accentElement,
    profileCard,
    profileCardWrapper,
    backgroundDecorations,
    parallaxLayers,
    holoGradient,
    holoSparkle,
    holoShine,
  } = elements

  // Desvanecer efecto holográfico
  const holoElements = [holoGradient, holoSparkle, holoShine].filter(Boolean)
  if (holoElements.length) {
    gsap.to(holoElements, { duration: 0.8, ease: 'power2.out', opacity: 0 })
  }

  if (accentElement) {
    gsap.to(accentElement, {
      duration: 1.2,
      ease: 'power3.out',
      backgroundPosition: '50% 50%',
    })
  }

  if (profileCard) {
    gsap.to(profileCard, {
      rotateY: 0,
      rotateX: 0,
      duration: 1.5,
      ease: 'elastic.out(1, 0.4)',
    })
  }

  if (profileCardWrapper) {
    gsap.to(profileCardWrapper, {
      background: 'linear-gradient(135deg, var(--color-charcoal) 0%, var(--color-deep-black) 100%)',
      duration: 0.5,
    })
  }

  if (backgroundDecorations?.length) {
    backgroundDecorations.forEach((decoration) => {
      gsap.to(decoration, { duration: 1.4, ease: 'power3.out', x: 0, y: 0 })
    })
  }

  if (parallaxLayers?.length) {
    parallaxLayers.forEach((layer) => {
      gsap.to(layer, { duration: 1.6, ease: 'power3.out', x: 0, y: 0 })
    })
  }
}

// ============================================================================
// DEVICE ORIENTATION (MOBILE)
// ============================================================================
const handleDeviceOrientation = (e) => {
  if (!elements.profileCard) return

  const { profileCard, profileCardWrapper, holoGradient, holoSparkle, holoShine } = elements

  // Calibrar la posición inicial la primera vez
  if (!isOrientationCalibrated) {
    initialOrientation = { beta: e.beta || 0, gamma: e.gamma || 0 }
    isOrientationCalibrated = true
    return
  }

  // Beta: inclinación frontal/trasera (-180 a 180)
  // Gamma: inclinación izquierda/derecha (-90 a 90)
  const beta = e.beta || 0
  const gamma = e.gamma || 0

  // Calcular la diferencia respecto a la posición inicial
  const deltaBeta = beta - initialOrientation.beta
  const deltaGamma = gamma - initialOrientation.gamma

  // Limitar el rango de rotación (-15 a 15 grados)
  const rotateX = Math.max(-15, Math.min(15, deltaBeta * 0.5))
  const rotateY = Math.max(-15, Math.min(15, deltaGamma * 0.8))

  // Calcular porcentajes para efectos holo (0-100)
  const xPercent = Math.max(0, Math.min(100, 50 + deltaGamma))
  const yPercent = Math.max(0, Math.min(100, 50 + deltaBeta * 0.5))

  // Rotación 3D de la tarjeta
  gsap.to(profileCard, {
    rotateX: -rotateX,
    rotateY: rotateY,
    duration: 0.3,
    ease: 'power2.out',
    transformPerspective: 1200,
  })

  // Efecto de luz en la tarjeta
  if (profileCardWrapper) {
    gsap.to(profileCardWrapper, {
      background: `linear-gradient(${135 + rotateY}deg, var(--color-charcoal) 0%, var(--color-deep-black) 100%)`,
      duration: 0.3,
    })
  }

  // Efecto holográfico
  if (holoGradient) {
    gsap.to(holoGradient, {
      duration: 0.3,
      ease: 'power2.out',
      opacity: 0.6,
      background: `
        linear-gradient(
          ${125 + rotateY * 2}deg,
          transparent 0%,
          rgba(180, 180, 190, 0.2) ${xPercent * 0.5}%,
          rgba(220, 220, 230, 0.25) ${xPercent}%,
          rgba(160, 160, 170, 0.2) ${xPercent * 1.2}%,
          rgba(200, 200, 210, 0.15) ${xPercent * 1.5}%,
          transparent 100%
        )
      `,
    })
  }

  if (holoSparkle) {
    gsap.to(holoSparkle, {
      duration: 0.2,
      ease: 'power1.out',
      opacity: 0.5,
      backgroundPosition: `${xPercent}% ${yPercent}%`,
    })
  }

  if (holoShine) {
    gsap.to(holoShine, {
      duration: 0.4,
      ease: 'power2.out',
      opacity: 0.4,
      background: `
        radial-gradient(
          circle at ${xPercent}% ${yPercent}%,
          rgba(255, 255, 255, 0.4) 0%,
          rgba(255, 255, 255, 0.1) 20%,
          transparent 60%
        )
      `,
    })
  }
}

const requestOrientationPermission = async () => {
  // iOS 13+ requiere permiso explícito
  if (
    typeof DeviceOrientationEvent !== 'undefined' &&
    typeof DeviceOrientationEvent.requestPermission === 'function'
  ) {
    try {
      const permission = await DeviceOrientationEvent.requestPermission()
      if (permission === 'granted') {
        hasOrientationPermission = true
        window.addEventListener('deviceorientation', handleDeviceOrientation)
      }
    } catch (error) {
      console.warn('DeviceOrientation permission denied:', error)
    }
  } else if ('DeviceOrientationEvent' in window) {
    // Android y navegadores que no requieren permiso
    hasOrientationPermission = true
    window.addEventListener('deviceorientation', handleDeviceOrientation)
  }
}

const initDeviceOrientation = () => {
  // Solo inicializar en dispositivos móviles con soporte
  if (!isMobile() || !('DeviceOrientationEvent' in window)) return

  // En iOS, necesitamos que el usuario interactúe primero
  if (typeof DeviceOrientationEvent.requestPermission === 'function') {
    // Agregar listener al primer toque para solicitar permiso
    const requestOnTouch = () => {
      requestOrientationPermission()
      document.removeEventListener('touchstart', requestOnTouch)
    }
    document.addEventListener('touchstart', requestOnTouch, { once: true, passive: true })
  } else {
    // Android - iniciar directamente
    requestOrientationPermission()
  }
}

// ============================================================================
// INICIALIZACIÓN Y LIMPIEZA
// ============================================================================
const initHeroAnimations = () => {
  cacheElements()
  setInitialStates()
  createEntranceTimeline()
  createFloatingAnimations()

  // Event listeners para desktop
  elements.hero.addEventListener('mousemove', handleMouseMove)
  elements.hero.addEventListener('mouseleave', handleMouseLeave)

  // Inicializar Device Orientation para móviles
  initDeviceOrientation()

  // Configurar transforms iniciales
  const allParallaxElements = [
    ...elements.backgroundDecorations,
    ...elements.parallaxLayers,
    elements.profileCard,
  ].filter(Boolean)
  gsap.set(allParallaxElements, { transformOrigin: 'center center', force3D: true })
}

const cleanup = () => {
  masterTimeline?.kill()
  floatingAnimations.forEach((anim) => anim.kill())
  floatingAnimations = []

  if (elements.hero) {
    elements.hero.removeEventListener('mousemove', handleMouseMove)
    elements.hero.removeEventListener('mouseleave', handleMouseLeave)
  }
  window.removeEventListener('scroll', handleScroll)

  // Limpiar Device Orientation
  if (hasOrientationPermission) {
    window.removeEventListener('deviceorientation', handleDeviceOrientation)
  }
}

// ============================================================================
// HANDLERS DE BOTONES
// ============================================================================
const handleViewWork = () => {
  gsap.to('.hero__content', {
    opacity: 0,
    x: -50,
    duration: 0.5,
    ease: 'power2.in',
  })
  gsap.to('.profile-card', {
    opacity: 0,
    scale: 0.9,
    duration: 0.5,
    ease: 'power2.in',
    onComplete: () => {
      // TODO: Navegar al portfolio
    },
  })
}

const handleViewResume = () => {
  window.location.href = '/resume'
}

// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================
onMounted(() => {
  initHeroAnimations()
  window.addEventListener('scroll', handleScroll)

  // Show tilt indicator after entrance animation if mobile/tablet
  setTimeout(() => {
    showTiltIndicator.value = true
  }, 2500)
})

onUnmounted(cleanup)
</script>
<style scoped>
/* ==========================================================================
   HERO SECTION - Bloque principal
   ========================================================================== */
.hero {
  @apply min-h-screen overflow-hidden relative;
  color: var(--color-text-primary);
  background-color: var(--color-deep-black);
  background-image: url('@/assets/images/noise-bg.webp');
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  background-blend-mode: darken;
}

:root:not(.dark) .hero {
  background-blend-mode: soft-light;
}

/* ==========================================================================
   HERO BACKGROUND - Fondo con parallax
   ========================================================================== */
.hero__background {
  @apply absolute inset-0 opacity-20 z-[1];
}

.hero__parallax-layer {
  @apply absolute inset-0 pointer-events-none will-change-transform z-[1];
}

.hero__parallax-layer--mid {
  @apply z-[2];
}

/* ==========================================================================
   HERO DECORATIONS - Elementos decorativos
   ========================================================================== */
.hero__decoration {
  @apply absolute rounded-full;
  will-change: transform;
  filter: blur(3rem);
}

.hero__decoration--top-left {
  @apply top-20 left-20 w-96 h-96 bg-electric-purple;
  opacity: 0.15;
}

.hero__decoration--bottom-right {
  @apply bottom-20 right-20 w-80 h-80 bg-electric-purple;
  opacity: 0.1;
}

.hero__decoration--center {
  @apply top-1/2 left-1/2 w-72 h-72 bg-steel -translate-x-1/2 -translate-y-1/2;
  opacity: 0.08;
}

/* ==========================================================================
   HERO CONTAINER & GRID
   ========================================================================== */
.hero__container {
  @apply max-w-6xl mx-auto px-5 sm:px-6 pt-20 pb-8 lg:py-0 relative z-10;
}

.hero__grid {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-10 items-center lg:min-h-screen;
}

/* ==========================================================================
   HERO CONTENT - Contenido de texto
   ========================================================================== */
.hero__content {
  @apply relative text-center lg:text-left flex flex-col gap-3 lg:gap-4 order-1 items-center lg:items-start;
}

.hero__header {
  @apply mb-2 lg:mb-4;
}

.hero__title {
  @apply text-[2.5rem] sm:text-5xl md:text-6xl font-medium leading-[1.1] sm:leading-tight text-center lg:text-left;
}

.hero__title-accent {
  @apply bg-clip-text text-transparent;
  background-image: linear-gradient(
    90deg,
    var(--color-electric-purple) 0%,
    var(--color-ash) 50%,
    var(--color-electric-purple) 100%
  );
  background-size: 400% 400%;
  background-position: 50% 50%;
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.hero__title-italic {
  @apply font-playfair italic;
}

.hero__description {
  @apply text-base sm:text-lg leading-relaxed max-w-lg text-steel text-center lg:text-left;
}

.hero__actions {
  @apply flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4;
}

/* ==========================================================================
   HERO SOCIAL - Enlaces sociales
   ========================================================================== */
.hero__social {
  @apply flex gap-5 sm:gap-6 pt-4 sm:pt-8;
}

.hero__social-link {
  @apply text-steel hover:text-electric-purple transition-colors duration-200 p-1.5 sm:p-1;
}

.hero__social-icon {
  @apply w-5 h-5 sm:w-6 sm:h-6;
}

/* ==========================================================================
   STATUS BADGE
   ========================================================================== */
.status-badge {
  @apply inline-flex items-center gap-2 backdrop-blur-sm border border-border rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs w-fit mb-2 lg:mb-6 bg-surface-elevated mx-auto md:mx-0;
}

:root:not(.dark) .status-badge {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.status-badge__dot {
  @apply w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-electric-purple animate-pulse;
}

.status-badge__text {
  @apply text-text-primary;
}

/* ==========================================================================
   HERO MEDIA - Sección de imagen
   ========================================================================== */
.hero__media {
  @apply flex justify-center lg:justify-end relative order-2 mt-8 lg:mt-0;
}

.hero__media::before {
  content: '';
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none lg:block;
  width: 200%;
  height: 200%;
  background-image:
    linear-gradient(to right, var(--color-border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--color-border) 1px, transparent 1px);
  background-size: 2rem 2rem;
  z-index: 0;
  mask-image: radial-gradient(ellipse 50% 50% at 50% 50%, black 40%, transparent 80%);
  -webkit-mask-image: radial-gradient(ellipse 50% 50% at 50% 50%, black 40%, transparent 80%);
}

/* ==========================================================================
   PROFILE CARD - Tarjeta de perfil
   ========================================================================== */
.profile-card {
  @apply relative m-0;
  perspective: 1000px;
  transform-style: preserve-3d;
  will-change: transform;
}

.profile-card__image-wrapper {
  @apply relative rounded-[3rem] overflow-hidden border;
  width: min(85vw, 20rem);
  height: min(110vw, 26rem);
  border-color: var(--color-border);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  background: linear-gradient(135deg, var(--color-charcoal) 0%, var(--color-deep-black) 100%);
  transform-style: preserve-3d;
  corner-shape: squircle;
}

@media (min-width: 640px) {
  .profile-card__image-wrapper {
    width: 24rem;
    height: 30rem;
  }
}

@media (min-width: 768px) {
  .profile-card__image-wrapper {
    width: 28.125rem;
    height: 36.25rem;
  }
}

@media (min-width: 1024px) {
  .profile-card__image-wrapper {
    width: 30rem;
    height: 38.75rem;
  }
}

:root:not(.dark) .profile-card__image-wrapper {
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
}

.profile-card__image {
  @apply w-full h-full object-cover object-center rounded-[2rem];
  corner-shape: squircle;
}

.profile-card__overlay {
  @apply absolute inset-0 rounded-[2rem];
  corner-shape: squircle;
}

.profile-card__overlay--shadow {
  background: linear-gradient(180deg, transparent 0%, rgba(12, 12, 12, 0.3) 100%);
}

.profile-card__badges {
  @apply absolute top-3 right-3 md:top-4 md:right-4 flex flex-col gap-1 md:gap-2 z-[2];
}

.profile-card__badge {
  @apply absolute -bottom-4 sm:-bottom-6 left-0 right-0 mx-auto max-w-[90%] sm:max-w-[80%];
}

.profile-card__decoration {
  @apply absolute rounded-full hidden sm:block;
  filter: blur(1rem);
}

.profile-card__decoration--top {
  @apply -top-4 -right-4 w-20 h-20;
  background-color: rgba(122, 63, 255, 0.2);
}

.profile-card__decoration--bottom {
  @apply -bottom-4 -left-4 w-16 h-16;
  background-color: rgba(90, 90, 90, 0.2);
}

/* ==========================================================================
   HOLOGRAPHIC EFFECT - Efecto holográfico
   ========================================================================== */
.profile-card__holo {
  @apply absolute inset-0 rounded-[2rem] overflow-hidden pointer-events-none z-[3] mix-blend-color-dodge;
  corner-shape: squircle;
}

.profile-card__holo-gradient {
  @apply absolute inset-0 opacity-0 transition-opacity duration-300;
  background: linear-gradient(
    125deg,
    transparent 0%,
    rgba(192, 192, 192, 0.15) 25%,
    rgba(220, 220, 220, 0.2) 50%,
    rgba(169, 169, 169, 0.15) 75%,
    transparent 100%
  );
}

.profile-card__holo-sparkle {
  @apply absolute inset-0 opacity-0 mix-blend-overlay;
  filter: blur(0.3px);
  background-image:
    radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 2%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.6) 0%, transparent 1.5%),
    radial-gradient(circle at 40% 70%, rgba(255, 255, 255, 0.7) 0%, transparent 1%),
    radial-gradient(circle at 60% 50%, rgba(255, 255, 255, 0.9) 0%, transparent 2.5%),
    radial-gradient(circle at 10% 80%, rgba(255, 255, 255, 0.5) 0%, transparent 1%),
    radial-gradient(circle at 90% 70%, rgba(255, 255, 255, 0.6) 0%, transparent 1.5%),
    radial-gradient(circle at 30% 10%, rgba(255, 255, 255, 0.8) 0%, transparent 2%),
    radial-gradient(circle at 70% 90%, rgba(255, 255, 255, 0.7) 0%, transparent 1%),
    radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.5) 0%, transparent 1.5%),
    radial-gradient(circle at 25% 55%, rgba(255, 255, 255, 0.6) 0%, transparent 1%);
  background-size: 200% 200%;
}

.profile-card__holo-shine {
  @apply absolute inset-0 opacity-0 mix-blend-soft-light;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0.1) 30%,
    transparent 70%
  );
}

.profile-card:hover .profile-card__holo-gradient {
  opacity: 0.3;
}

/* ==========================================================================
   PROFILE BADGE - Badge flotante
   ========================================================================== */
.profile-badge {
  @apply flex items-center gap-3 backdrop-blur-sm border border-border rounded-[2rem] px-6 py-3 bg-surface-elevated;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  corner-shape: squircle;
}

:root:not(.dark) .profile-badge {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.profile-badge__icon {
  @apply w-8 h-8 rounded-[2rem] flex items-center justify-center bg-gradient-to-br from-electric-purple to-ash;
  corner-shape: squircle;
}

.profile-badge__icon svg {
  @apply w-4 h-4;
}

.profile-badge__content {
  @apply text-left;
}

.profile-badge__title {
  @apply font-medium text-sm m-0 text-ash;
}

.profile-badge__text {
  @apply text-xs m-0 text-steel;
}

/* ==========================================================================
   EXPERIENCE BADGE
   ========================================================================== */
.experience-badge {
  @apply inline-flex items-center gap-1 md:gap-2 py-1.5 px-2.5 md:py-2 md:px-3 rounded-full backdrop-blur-sm border;
  border-color: rgba(122, 63, 255, 0.2);
  background: var(--color-surface-elevated);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}

:root:not(.dark) .experience-badge {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.experience-badge__dot {
  @apply w-2 h-2 rounded-full;
  background: linear-gradient(135deg, var(--color-electric-purple), var(--color-ash));
  box-shadow: 0 0 10px rgba(122, 63, 255, 0.6);
}

.experience-badge__text {
  @apply text-[0.65rem] md:text-[0.72rem] leading-none whitespace-nowrap tracking-wide;
  color: var(--color-ash);
}

/* ==========================================================================
   ANIMATIONS
   ========================================================================== */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

/* ==========================================================================
   SCROLL INDICATOR
   ========================================================================== */
.scroll-indicator {
  @apply absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden lg:flex pointer-events-none;
}

.scroll-indicator__content {
  @apply flex flex-col items-center gap-3;
}

.scroll-indicator__text {
  @apply text-xs uppercase tracking-[0.3em] text-steel font-medium;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  animation: fadeInOut 2.5s ease-in-out infinite;
}

.scroll-indicator__line {
  @apply relative w-[1px] h-16 overflow-hidden;
  background: linear-gradient(to bottom, var(--color-border) 0%, transparent 100%);
}

.scroll-indicator__dot {
  @apply absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full;
  background: var(--color-electric-purple);
  box-shadow:
    0 0 10px var(--color-electric-purple),
    0 0 20px var(--color-electric-purple);
  animation: scrollDot 2s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}

@keyframes scrollDot {
  0% {
    top: 0;
    opacity: 0;
    transform: translateX(-50%) scale(0.5);
  }
  15% {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
  85% {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
  100% {
    top: 100%;
    opacity: 0;
    transform: translateX(-50%) scale(0.5);
  }
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}
</style>
